import React from 'react';
import { StylesProps } from 'src/Library/Types';
import withStyles from 'react-jss';
import { DiscoverRatesFiltersStyles } from './DiscoverRatesFilters.styes';
import { Form, Select, Tooltip, Spin, InputNumber } from 'antd';
import { connect } from 'react-redux';
import { SelectedFilters } from './TypesInDiscoverRates/SelectedFilters';
import { SEConsignorAddress } from '../shipment-execution/SE.interfaces';
import { useGenericState } from 'src/Library/UseGenericState';
import { InquiryModes } from '../Enquiry/InquiryUtils';
import { bindActionCreators } from 'redux';
import { getAllDataSetFilter } from 'src/actions/discoverRatesActions';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends StylesProps<ReturnType<typeof DiscoverRatesFiltersStyles>> {
  service?: { key: string, type?: string[], serviceType?: string };
  form: any;
  selectedFilters: SelectedFilters;
  stuffingAndDestuffingLocation: string[];
  orgType: string;
  stuffingAddresses: SEConsignorAddress[];
  deStuffingAddresses : SEConsignorAddress[];
  mode: string;
  getAllDataSetFilter: (val, apiCall?) => void;
  disableRateDiscoveryServicesLinking: boolean;
}

interface StateProps {
  isStuffDeStuffTypeChanged?: boolean;
}

const DiscoverRatesServicesFilterDetails = (props: IProps) : React.ReactElement => {

  const {
    service,
    form,
    classes,
    selectedFilters,
    stuffingAndDestuffingLocation,
    stuffingAddresses,
    deStuffingAddresses,
    mode,
    getAllDataSetFilter,
    disableRateDiscoveryServicesLinking,
  } = props;

  const [state, setState] = useGenericState<StateProps>({ });

  const { isStuffDeStuffTypeChanged } = state;

  React.useEffect(() => {
    // to remove the required message from weight field (if visible) when factory is selected
    const isFactorySelected = form.getFieldValue(service?.key === 'originServices' ? 'stuffingType' : 'destuffingType') === 'Factory';
    if (isFactorySelected) {
      const weightKey = service?.key === 'originServices' ? 'stuffingWeight' : 'destuffingWeight';
      form.validateFields([weightKey], (err) => {
        if (err) return;
      });
    }
  }, [isStuffDeStuffTypeChanged]);

  const isTransportationService = service?.serviceType === 'Transportation Service' || service?.serviceType === 'Freight Service';

  const renderStuffDeStuffType = () => {
    const label = service?.key === 'originServices' ? <>Stuffing Type<Asterisk /></> : <>De-Stuffing Type<Asterisk /></>;
    const key = service?.key === 'originServices' ? 'stuffingType' : 'destuffingType';
    return (
      <div className={classes.rowDiv}>
        <div style={{ width: '34%', marginTop: '6px' }}>
          <span className={classes.serviceFilterLabel}>{label}</span>
        </div>
        <Form.Item style={{ marginBottom: '4px', width: '66%', paddingLeft: '6px' }}>
          {form.getFieldDecorator(key,
            { 
              initialValue: selectedFilters[key],
              rules: [{required: true, message: 'Required'}]
            }
          )(
            <Select
              allowClear
              style={{ fontSize: 12, width: '100%' }}
              placeholder="Enter Type"
              dropdownMatchSelectWidth={false}
              onChange={() => setState({ isStuffDeStuffTypeChanged: !isStuffDeStuffTypeChanged })}
            >
              {stuffingAndDestuffingLocation?.map((item) => 
                <Select.Option key={item}>{item}</Select.Option>
              )}
            </Select>
          )}
        </Form.Item>
      </div>
    );
  };

  const handleLocationChange = (locationId, type) => {
    if (mode === InquiryModes.FCL && disableRateDiscoveryServicesLinking) {
      if (type === 'origin') {
        stuffingAddresses?.forEach(item => {
          if (item.id === locationId) {
            getAllDataSetFilter({ stuffingType: "Factory" }, false);
          }
        });
      }
      if (type === 'destination') {
        deStuffingAddresses?.forEach(item => {
          if (item.id === locationId) {
            getAllDataSetFilter({ destuffingType: "Factory" }, false);
          }
        });
      }      
    }
  }

  const renderStuffDeStuffLocation = () => {
    const isOriginService = service?.key === 'originServices';
    const label = isOriginService ? <>Origin/pickup address<Asterisk /></> : <>Destination/delivery address<Asterisk /></>;
    const key = isOriginService ? 'stuffingLocationId' : 'destuffingLocationId';
    const isStuffDeStuffLocationRequired = service?.serviceType === 'Transportation Service' || service?.serviceType === 'Freight Service' ||
      form.getFieldValue(isOriginService ? 'stuffingType' : 'destuffingType') === 'Factory';

    return (
      <div className={classes.rowDiv}>
        <div style={{ width: '34%', marginTop: '6px' }}>
          <span className={classes.serviceFilterLabel}>{label}</span>
        </div>
        <Form.Item style={{ marginBottom: '4px', width: '66%', paddingLeft: '6px' }}>
          {form.getFieldDecorator(key,
            {
              initialValue: selectedFilters[key],
              rules: [{ required: isStuffDeStuffLocationRequired, message: 'Required' }]
            }
          )(
            <Select
              allowClear
              style={{ width: '100%' }}
              dropdownMatchSelectWidth={false}
              placeholder="Enter Location"
              showSearch
              optionFilterProp="data-address"
              onChange={(event)=>handleLocationChange(event, isOriginService ? 'origin' : 'destination')}
            >
              {(isOriginService ? stuffingAddresses : deStuffingAddresses)?.map((item) => (
                <Select.Option key={item.id} data-address={item.displayAddress}>
                  <Tooltip title={item.displayAddress}>
                    <span style={{ marginRight: '5px' }}>
                      {`${item.name}, ${item.pincode ?? '-'}`}
                    </span>
                  </Tooltip>
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </div>
    );
  };

  const renderWeight = () => {
    const weightKey = service?.key === 'originServices' ? 'stuffingWeight' : 'destuffingWeight';
    const unitKey = service?.key === 'originServices' ? 'stuffingUnit' : 'destuffingUnit';
    const isWeightRequired = ((service?.serviceType === 'Transportation Service' || service?.serviceType === 'Freight Service') &&
      form.getFieldValue(service?.key === 'originServices' ? 'stuffingType' : 'destuffingType') === 'Dock/CFS/Customs Warehouse')
      || form.getFieldValue(unitKey);
    return (
      <div className={classes.rowDiv}>
        <div style={{ width: '34%', marginTop: '6px' }}>
          <span className={classes.serviceFilterLabel}>Weight{isWeightRequired ? <Asterisk /> : null}</span>
        </div>
        <Form.Item className={classes.weight} style={{  width: '41%' }}>
          {form.getFieldDecorator(weightKey,
            {
              initialValue: selectedFilters?.[weightKey]?.value,
              rules: [{ required: isWeightRequired, message: 'Required' }]
            }
            )(
              <InputNumber
                type="number"
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder={'Enter weight'}
              />
            )}
        </Form.Item>
        <Form.Item className={classes.weight} style={{  width: '25%' }}>
          {form.getFieldDecorator(unitKey,
          {
            initialValue: selectedFilters?.[weightKey]?.unit,
            rules: [{ required: form.getFieldValue(weightKey), message: 'required' }]
          }
          )(
          <Select
            allowClear
            style={{fontSize: 12, width: '100%'}}
            placeholder="unit"
            dropdownMatchSelectWidth={false}
          >
            {['kg', 'mt'].map((item) => {
              return (
                <Select.Option key={item}>
                  {item}
                </Select.Option>
              );
            })}
          </Select>
          )}
        </Form.Item>
      </div>
    );
  };

  return (
    <React.Fragment>
      {(mode === InquiryModes.FCL && !disableRateDiscoveryServicesLinking) && renderStuffDeStuffType()}
      {isTransportationService &&
        <React.Fragment>
          {renderStuffDeStuffLocation()}
          {!disableRateDiscoveryServicesLinking && renderWeight()}
        </React.Fragment>
      }
    </React.Fragment>
  );
};

const mapStateToProps = ({ discoverRatesReducer, masterData }, ownProps) => ({
  selectedFilters: discoverRatesReducer.selectedFilters,
  stuffingAndDestuffingLocation: masterData.stuffingAndDestuffingLocation,
  disableRateDiscoveryServicesLinking: masterData.modules.disable_rate_discovery_services_linking || false,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ getAllDataSetFilter },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(DiscoverRatesFiltersStyles)(DiscoverRatesServicesFilterDetails));
